import { Button } from "@/components/ui/button";
import {
  ArrowRight,
  Phone,
  Calendar,
  Award,
  Leaf,
  Shield,
  Star,
} from "lucide-react";
import { motion, useInView } from "framer-motion";
import { useEffect, useState, useRef } from "react";
import ContactConciergeModal from "./ContactConciergeModal";
import { useLanguage } from "@/contexts/LanguageContext";
import RatingDisplay from "./RatingDisplay";

// Animated Counter Component
const AnimatedCounter = ({ t }: { t: (key: string) => string }) => {
  const [count, setCount] = useState(100000);
  const startCount = 100000;
  const targetCount = 250000;
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  useEffect(() => {
    if (isInView) {
      const duration = 3000; // 3 seconds
      const startTime = Date.now();
      const countRange = targetCount - startCount; // 150,000

      const animate = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);

        // Easing function for smooth animation
        const easeOutQuart = 1 - Math.pow(1 - progress, 4);
        const currentCount = Math.floor(startCount + countRange * easeOutQuart);

        setCount(currentCount);

        if (progress < 1) {
          requestAnimationFrame(animate);
        }
      };

      requestAnimationFrame(animate);
    }
  }, [isInView, targetCount, startCount]);

  return (
    <motion.div
      ref={ref}
      className="bg-elegant-white/10 backdrop-blur-sm rounded-2xl p-8 md:p-10 border border-elegant-white/20"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      viewport={{ once: true }}
      data-oid="su0w18s"
    >
      <motion.div
        className="text-4xl md:text-5xl lg:text-6xl font-semibold text-elegant-white mb-2"
        initial={{ scale: 0.8 }}
        whileInView={{ scale: 1 }}
        transition={{ duration: 0.5, delay: 0.2 }}
        viewport={{ once: true }}
        data-oid="inwhpyk"
      >
        {count.toLocaleString()}+
      </motion.div>
      <motion.p
        className="font-playfair text-xl md:text-2xl text-elegant-white/90 uppercase tracking-wide"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.6, delay: 0.4 }}
        viewport={{ once: true }}
        data-oid="13:6qwe"
      >
        {t("cta.happyGuests")}
      </motion.p>
      <motion.p
        className="font-lato text-elegant-white/70 mt-2 text-sm md:text-base"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.6, delay: 0.6 }}
        viewport={{ once: true }}
        data-oid="cl-7_.7"
      >
        {t("cta.trustedByGuests")}
      </motion.p>
    </motion.div>
  );
};

const CTASection = () => {
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  // Safe hook usage with fallback
  let t: (key: string) => string;
  try {
    const { t: useLanguageT } = useLanguage();
    t = useLanguageT;
  } catch (error) {
    // Fallback to English translations if context is not available
    t = (key: string) => {
      const fallbackTranslations: Record<string, string> = {
        "cta.title": "Ready to Create Your Perfect Alpine Escape?",
        "cta.description":
          "Experience the magic of Swiss Alpine luxury, world-class wellness programs, and breathtaking natural beauty at Beatenberg Resort & Spa.",
        "cta.bookYourStay": "Book Your Stay",
        "cta.contactConcierge": "Contact Our Concierge",
        "cta.happyGuests": "Happy Guests",
        "cta.trustedByGuests": "Trusted by guests worldwide",
        "cta.certifiedExcellence": "Certified Excellence",
        "cta.certifiedExcellenceDesc":
          "Recognized for outstanding service and quality",
        "cta.gastrosuisseRated": "Gastrosuisse Rated",
        "cta.swissHotelAssociation": "Swiss Hotel Association",
        "cta.swisstainableCommitted": "Swisstainable Committed",
        "cta.qualityAssurance": "Quality Assurance",
      };
      return fallbackTranslations[key] || key;
    };
  }

  return (
    <section
      id="contact"
      className="pt-8 pb-20 bg-gradient-alpine"
      data-oid="-49s3w4"
    >
      <div
        className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center"
        data-oid="7s39rq."
      >
        <h2
          className="font-playfair text-4xl md:text-5xl text-elegant-white mb-6 uppercase"
          data-oid="hrisg0:"
        >
          {t("cta.title")}
        </h2>

        <p
          className="font-lato text-xl text-elegant-white/80 mb-12 max-w-2xl mx-auto leading-relaxed"
          data-oid="vcnj0k-"
        >
          {t("cta.description")}
        </p>

        <div
          className="flex flex-col sm:flex-row gap-6 justify-center items-center"
          data-oid="4:-_7aq"
        >
          <Button
            asChild
            variant="elegant"
            size="xl"
            className="min-w-[250px] bg-elegant-white text-pine-green hover:bg-elegant-white/90 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
            trackingType="booking"
            trackingLabel="Book Your Stay - CTA Section"
            trackingLocation="Homepage CTA"
            data-oid="uijo3wp"
          >
            <a
              href={import.meta.env.VITE_BOOKING_URL}
              data-oid="-69v6za"
            >
              <Calendar size={20} className="mr-2" data-oid="m2lh3ry" />
              {t("cta.bookYourStay")}
              <ArrowRight size={16} className="ml-2" data-oid="k45kp94" />
            </a>
          </Button>

          <Button
            variant="elegant"
            size="xl"
            className="min-w-[250px] bg-elegant-white/10 backdrop-blur-sm border-elegant-white/30 text-elegant-white hover:bg-elegant-white hover:text-pine-green rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
            onClick={() => setIsContactModalOpen(true)}
            trackingType="contact_modal"
            trackingLabel="Contact Concierge"
            trackingLocation="homepage_cta"
            trackingData={{ modalType: "concierge" }}
            data-oid="u2epf9s"
          >
            <Phone size={20} className="mr-2" data-oid="fzoge8z" />
            {t("cta.contactConcierge")}
          </Button>
        </div>

        {/* Rating Display */}
        <div className="mt-6" data-oid="fvz-9-t">
          <RatingDisplay
            rating={4.2}
            reviewCount={245}
            type="hotel"
            className="justify-center"
            data-oid="wynafrr"
          />
        </div>

        {/* Happy Visitors Counter */}
        <div className="mt-16 mb-12" data-oid="g5joupy">
          <AnimatedCounter t={t} data-oid="h0lra:w" />
        </div>

        <div className="mt-12" data-oid="1-z47gp">
          <div className="text-center mb-8" data-oid="1idmxcn">
            <h3
              className="font-playfair text-2xl font-semibold text-elegant-white mb-2"
              data-oid="7u8o_8p"
            >
              {t("cta.certifiedExcellence")}
            </h3>
            <p className="font-lato text-elegant-white/80" data-oid=".43-a1b">
              {t("cta.certifiedExcellenceDesc")}
            </p>
          </div>

          <div
            className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 max-w-6xl mx-auto"
            data-oid="8t:iz04"
          >
            {[
              {
                name: t("cta.gastrosuisseRated"),
                description: "4-Star Deluxe Certification",
                icon: Star,
                color: "text-white",
                iconCount: 4,
              },
              {
                name: t("cta.swissHotelAssociation"),
                description: "Premium Member Status",
                icon: Award,
                color: "text-white",
              },
              {
                name: t("cta.swisstainableCommitted"),
                description: "Committed",
                icon: Leaf,
                color: "text-white",
              },
              {
                name: t("cta.qualityAssurance"),
                description: "Certified Excellence",
                icon: Shield,
                color: "text-white",
              },
            ].map((cert, index) => {
              const Icon = cert.icon;
              return (
                <div
                  key={index}
                  className="text-center group cursor-pointer"
                  data-oid="kg_ec3q"
                >
                  <div
                    className="bg-elegant-white/10 backdrop-blur-sm rounded-xl p-4 md:p-6 hover:bg-elegant-white/20 transition-all duration-300 hover:scale-105 h-full min-h-[140px] md:min-h-[160px] flex flex-col justify-center"
                    data-oid="0-2s4sz"
                  >
                    {/* Icon - Fixed height container for alignment */}
                    <div
                      className="h-12 flex items-center justify-center mb-4"
                      data-oid="4js4zga"
                    >
                      {cert.iconCount ? (
                        // Multiple stars for Gastrosuisse
                        <div className="flex gap-1" data-oid="2mz.6fb">
                          {Array.from({ length: cert.iconCount }, (_, i) => (
                            <Icon
                              key={i}
                              className={`${cert.color} group-hover:scale-110 transition-transform duration-300`}
                              size={20}
                              data-oid="o6274nz"
                            />
                          ))}
                        </div>
                      ) : (
                        // Single icon for others
                        <Icon
                          className={`${cert.color} group-hover:scale-110 transition-transform duration-300`}
                          size={28}
                          data-oid="p6yvbia"
                        />
                      )}
                    </div>

                    {/* Title - Fixed height container for alignment */}
                    <div
                      className="h-12 flex items-center justify-center"
                      data-oid="knyh0.c"
                    >
                      <h4
                        className="font-playfair font-semibold text-elegant-white text-sm md:text-base lg:text-lg leading-tight text-center"
                        data-oid="t7r1:xe"
                      >
                        {cert.name}
                      </h4>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Contact Concierge Modal */}
      <ContactConciergeModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
        data-oid="peo.cm2"
      />
    </section>
  );
};

export default CTASection;
